import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

// Renovação de token em andamento (compartilhada entre chamadas que falham juntas)
let refreshEmAndamento = null;

// Bloqueio por IP: o administrador pode ativá-lo com usuários já logados. Em vez de as telas
// esvaziarem sem explicação, avisa e só depois desconecta.
export const IP_BLOCK_MESSAGE = 'Acesso não permitido a partir desta rede';
export const IP_BLOCK_STYLE = { background: '#173efc', border: '1px solid #173efc', color: '#fff', fontWeight: 700 };
const IP_BLOCK_LOGOUT_DELAY_MS = 8000;
let ipBlockLogoutScheduled = false;

const handleIpBlockedSession = (clearUser) => {
  if (ipBlockLogoutScheduled) return; // várias requisições falham juntas; avisa uma vez só
  ipBlockLogoutScheduled = true;
  toast.error(
    'A sua rede atual não está autorizada a acessar o sistema. ' +
      `Você será desconectado em ${IP_BLOCK_LOGOUT_DELAY_MS / 1000} segundos. Salve o que estiver fazendo.`,
    { duration: IP_BLOCK_LOGOUT_DELAY_MS, style: IP_BLOCK_STYLE }
  );
  setTimeout(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    clearUser();
    window.location.href = '/login';
  }, IP_BLOCK_LOGOUT_DELAY_MS);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      // Limpar temp_token de sessões incompletas
      if (localStorage.getItem('temp_token')) {
        localStorage.removeItem('temp_token');
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Configurar interceptor do Axios para detectar sessão expirada
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // Se a resposta for bem-sucedida, apenas retorna
        return response;
      },
      async (error) => {
        // Se houver erro 401 (Unauthorized), significa que a sessão expirou
        if (error.response && error.response.status === 401) {
          const requestUrl = error.config?.url || '';

          // O token de acesso dura 2 h; antes de deslogar, tenta renová-lo com o refresh token
          // (uma vez por requisição) e repete a chamada que falhou.
          const original = error.config;
          const refreshToken = localStorage.getItem('refresh_token');
          if (
            original && refreshToken && !original._retry &&
            !requestUrl.includes('/auth/login') &&
            !requestUrl.includes('/auth/refresh') &&
            !requestUrl.includes('/system/init')
          ) {
            original._retry = true;
            try {
              // Várias chamadas podem falhar juntas: só uma renovação por vez
              if (!refreshEmAndamento) {
                refreshEmAndamento = axios
                  .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
                  .then((res) => res.data)
                  .finally(() => { refreshEmAndamento = null; });
              }
              const novo = await refreshEmAndamento;
              localStorage.setItem('token', novo.access_token);
              if (novo.refresh_token) localStorage.setItem('refresh_token', novo.refresh_token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${novo.access_token}`;
              original.headers = { ...original.headers, Authorization: `Bearer ${novo.access_token}` };
              return axios(original);
            } catch (refreshError) {
              console.log('🔒 Não foi possível renovar a sessão:', refreshError?.response?.data?.detail || refreshError.message);
              // segue para o logout abaixo
            }
          }

          // Não mostrar mensagem se for tentativa de login falhada
          if (!requestUrl.includes('/auth/login') && !requestUrl.includes('/system/init')) {
            console.log('🔒 Sessão expirada detectada - redirecionando para login');

            // Limpar dados do usuário
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            
            // Mostrar mensagem de sessão expirada
            toast.error('Sua sessão expirou. Por favor, faça login novamente.', {
              duration: 5000,
            });
            
            // Redirecionar para login após 1 segundo
            setTimeout(() => {
              window.location.href = '/login';
            }, 1000);
          }
        }
        
        // Bloqueio por IP ativado com a sessão aberta: avisa e só depois desconecta.
        // Só este 403 (não outros) e nunca na tela de login (lá quem avisa é o próprio login).
        const detail = error.response?.data?.detail;
        if (
          error.response?.status === 403 &&
          typeof detail === 'string' &&
          detail.includes(IP_BLOCK_MESSAGE) &&
          !(error.config?.url || '').includes('/auth/login') &&
          window.location.pathname !== '/login'
        ) {
          handleIpBlockedSession(() => setUser(null));
        }

        return Promise.reject(error);
      }
    );

    // Cleanup: remover interceptor quando o componente desmontar
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData, password_must_change, force_password_reset, password_expired } = response.data;

      // Se precisar trocar senha, salva token temporário mas não autentica
      if (password_must_change || force_password_reset || password_expired) {
        localStorage.setItem('temp_token', access_token);
        if (refresh_token) localStorage.setItem('temp_refresh_token', refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        return { 
          success: true,
          password_must_change: password_must_change || false,
          force_password_reset: force_password_reset || false,
          password_expired: password_expired || false,
          user: userData
        };
      }
      
      // Fluxo normal: autentica o usuário
      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);

      return {
        success: true,
        password_must_change: false,
        force_password_reset: false,
        password_expired: false
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const finalizeLogin = (userData) => {
    const tempToken = localStorage.getItem('temp_token');
    if (tempToken) {
      localStorage.setItem('token', tempToken);
      localStorage.removeItem('temp_token');
      const tempRefresh = localStorage.getItem('temp_refresh_token');
      if (tempRefresh) {
        localStorage.setItem('refresh_token', tempRefresh);
        localStorage.removeItem('temp_refresh_token');
      }
    }
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('temp_refresh_token');
    localStorage.removeItem('temp_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const initializeSystem = async () => {
    try {
      const response = await axios.post(`${API}/system/init`);
      return response.data;
    } catch (error) {
      console.error('System initialization failed:', error);
      return { success: false, error: 'Failed to initialize system' };
    }
  };

  const value = {
    user,
    login,
    logout,
    finalizeLogin,
    loading,
    initializeSystem,
    isAdmin: user?.is_admin || false,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};